/**
 * THE PHONE'S DOSE SENTENCE — W66 step 2 (2026-09-24), the TypeScript twin of
 * `CoachSetDose(rows:).overviewLine` (HybridTrainer/Services/CoachSetDose.swift), which is the
 * bridge's own set mapping into `AssistLoad.prescriptionLine`: what the client's workout overview
 * prints under an exercise's name. `3 × 5 reps @ 60 kg`, `8/6/4 reps`, `2 × 1:30` — and no rest,
 * which the builder's `collapsedLabel` adds and the phone never prints there.
 *
 * 🔴 PROVEN AGAINST THE SWIFT, NEVER AGAINST TYPED SENTENCES (Fable's condition 3):
 * `phoneDose.test.ts` runs every case in `__fixtures__/doseCorpus.json` — sentences the Swift
 * printed (`tools/dose-corpus/build.py`, every distinct live set shape plus edge cases) — and a
 * second ruler recomputes the Swift sources' hash, so the corpus cannot go stale unseen.
 *
 * Coach prescriptions carry no assistance and no find-your-hold note, and the overview speaks
 * warm-up rungs silently — so those three branches of `prescriptionLine` are not ported; the
 * corpus would show the difference if a coach row ever reached them.
 */

import { distanceText } from "./distanceCell";
import { timeText } from "./typedCell";

export type DoseRow = {
  reps: number | null;
  time_seconds: number | null;
  weight_kg: number | null;
  set_type: string | null;
  /** W136 B2 S3 — `coach_workout_sets.rounds` / `distance_m` (metres); absent = not prescribed. */
  rounds?: number | null;
  distance_m?: number | null;
};

/** W136 B2 S3 — `AssistLoad.runPhrase`: a run or a rounds row in §5's words (`400 m`, `5 km`, `3 × 400 m in 1:45`,
 *  `40 m @ 32 kg`, `7 rounds + 5 reps`, `5 rounds in 14:30`); null when no typed column is complete. */
export function runPhrase(sets: number, distanceM: number[], rounds: number[], reps: number[], timeSeconds: number[],
                          distanceKm: boolean): string | null {
  if (distanceM.length === 0 && rounds.length === 0 && timeSeconds.length === 0) return null;
  const uniform = (texts: string[]) => texts.every((t) => t === texts[0]);
  const counted = (texts: string[], unit: string) =>
    uniform(texts) ? (texts.length > 1 ? `${String(texts.length)} × ` : "") + texts[0]! + unit : texts.join("/") + unit;
  let line: string;
  if (distanceM.length > 0) {
    const unit = distanceKm ? "km" : "m";
    line = counted(distanceM.map((m) => distanceText(m, unit)), ` ${unit}`);
  } else if (rounds.length > 0) {
    const r = rounds.map(String);
    line = counted(r, uniform(r) && rounds[0] === 1 ? " round" : " rounds");
    if (reps.length > 0) {
      const p = reps.map(String);
      line += " + " + (uniform(p) ? p[0]! : p.join("/")) + (uniform(p) && reps[0] === 1 ? " rep" : " reps");
    }
  } else {
    line = sets === 1 ? "1 set" : `${String(sets)} sets`;
  }
  if (timeSeconds.length > 0) {
    const t = timeSeconds.map((s) => timeText(s));
    line += " in " + (uniform(t) ? t[0]! : t.join("/"));
  }
  return line;
}

/** `EnginePerSet.column`: the values when EVERY row has one, else empty — never a short column. */
function column<T>(values: readonly (T | null)[]): T[] {
  return values.length === 0 || values.some((v) => v === null) ? [] : (values as T[]);
}

/** `AssistLoad.kg`: a whole number bare, else two decimals with the trailing zeros taken off. */
export function kg(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

/** `HoldTimerPlan.durationLabel`: `45s`, `1 min`, `1:30`. */
export function durationLabel(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (remainder === 0) return `${String(minutes)} min`;
  return `${String(minutes)}:${String(remainder).padStart(2, "0")}`;
}

/** `fields` — the exercise's `tracking_fields` (W136 B2 S3): a run / rounds row and its km. Omitted = no coach fields. */
export function phoneDose(rows: readonly DoseRow[], fields?: readonly string[] | null, untilLost = false): string {
  // ── CoachSetDose.init ──
  const types = rows.map((r) => r.set_type ?? "regular");
  const working = rows.filter((_, i) => types[i] !== "warm_up");
  const dose = working.length === 0 ? rows : working;
  const nums = (xs: (number | null)[]) => xs.filter((x): x is number => x !== null);
  const doseReps = nums(dose.map((r) => r.reps));
  const doseHolds = nums(dose.map((r) => r.time_seconds));
  const doseWeights = nums(dose.map((r) => r.weight_kg));
  const min = (xs: number[]) => (xs.length === 0 ? null : Math.min(...xs));
  const max = (xs: number[]) => (xs.length === 0 ? null : Math.max(...xs));
  const sets = Math.max(rows.length, 1);
  const repsLow = min(doseReps), repsHigh = max(doseReps);
  const holdLow = min(doseHolds), holdHigh = max(doseHolds);
  const target = max(doseWeights);
  let perSetReps = column(rows.map((r) => r.reps));
  let perSetSeconds = column(rows.map((r) => r.time_seconds));
  let perSetWeight = column(rows.map((r) => r.weight_kg));
  let perSetDistance = column(rows.map((r) => r.distance_m ?? null));
  let perSetRounds = column(rows.map((r) => r.rounds ?? null));

  // ── CoachSetDose.overviewLine: LoadFieldPolicy.isTypedTimeRow + splitTime (W136 B2 S3) ──
  const f = fields ?? [];
  const typed = perSetDistance.length > 0 || perSetRounds.length > 0
    || f.some((x) => x === "distance_m" || x === "distance_km" || x === "rounds");
  let perSetTime: number[] = [];
  let holdLowS = holdLow, holdHighS = holdHigh;
  if (typed) { perSetTime = perSetSeconds; perSetSeconds = []; holdLowS = null; holdHighS = null; }

  // ── AssistLoad.workingSplit ──
  let wSets = sets;
  let workingTypes: string[] = types;
  if (sets > 0 && types.length > 0) {
    const keepIdx = [...Array(sets).keys()].filter((i) => !(i < types.length && types[i] === "warm_up"));
    const warmUps = sets - keepIdx.length;
    if (warmUps > 0 && keepIdx.length > 0) {
      const keep = <T,>(col: T[]): T[] => (col.length === sets ? keepIdx.map((i) => col[i]!) : []);
      wSets = keepIdx.length;
      workingTypes = keepIdx.map((i) => types[i]!);
      perSetReps = keep(perSetReps); perSetSeconds = keep(perSetSeconds); perSetWeight = keep(perSetWeight);
      // `AssistLoad.workingColumn` — the same filter for the three typed columns
      perSetDistance = keep(perSetDistance); perSetRounds = keep(perSetRounds); perSetTime = keep(perSetTime);
    }
  }

  // ── AssistLoad.setsPhrase ──
  const spoken = <T,>(values: T[], unit: string, format: (v: T) => string): string | null => {
    const first = values[0];
    if (first === undefined) return null;
    if (values.every((v) => v === first)) return `${String(values.length)} × ${format(first)}${unit}`;
    return values.map(format).join("/") + unit;
  };
  // ── AssistLoad.failureRepsPhrase — a set to failure says its reps as a floor (`6/6+ reps`, his 5 Oct note) ──
  const failing = workingTypes.map((t) => t === "failure");
  const failurePhrase = (): string | null => {
    if (perSetReps.length === 0 || failing.length !== perSetReps.length || !failing.includes(true)) return null;
    const texts = perSetReps.map((r, i) => String(r) + (failing[i] === true ? "+" : ""));
    return texts.every((t) => t === texts[0]) ? `${String(texts.length)} × ${texts[0]!} reps` : `${texts.join("/")} reps`;
  };
  // ── AssistLoad.untilLostPhrase — the BALANCE clock (his yes 2026-10-07): a row marked `hold_until_lost` is `N × until lost`,
  // its head whatever its sets carry, because a balance hold has no target ──
  let head: string | null = untilLost ? (wSets > 0 ? `${String(wSets)} × until lost` : null)
    : runPhrase(wSets, perSetDistance, perSetRounds, perSetReps, perSetTime, f.includes("distance_km"))
    ?? failurePhrase() ?? spoken(perSetReps, " reps", (v) => String(v)) ?? spoken(perSetSeconds, "", durationLabel);
  if (head === null && wSets > 0 && !untilLost) {
    if (repsLow !== null && repsHigh !== null && repsLow !== repsHigh) head = `${String(wSets)} × ${String(repsLow)}–${String(repsHigh)} reps`;
    else if (repsLow !== null) head = `${String(wSets)} × ${String(repsLow)} reps`;
    else if (holdLowS !== null) {
      if (holdHighS !== null && holdHighS > holdLowS) {
        const range = holdHighS < 60 ? `${String(holdLowS)}–${String(holdHighS)}s` : `${durationLabel(holdLowS)}–${durationLabel(holdHighS)}`;
        head = `${String(wSets)} × ${range} hold`;
      } else head = `${String(wSets)} × ${durationLabel(holdLowS)} hold`;
    } else head = wSets === 1 ? "1 set" : `${String(wSets)} sets`;
  }

  // ── AssistLoad.loadClause / clause ──
  let load: string | null;
  const firstW = perSetWeight[0];
  if (firstW !== undefined && !perSetWeight.every((w) => w === firstW)) load = `@ ${perSetWeight.map(kg).join("/")} kg`;
  else {
    const t = target ?? firstW ?? null;
    load = t !== null && t > 0 ? `@ ${kg(t)} kg` : null;
  }

  // ── AssistLoad.prescriptionLine ──
  if (head === null && load === null) return "";
  if (head === null) return load!;
  if (load === null) return head;
  return head + (load.startsWith("@") ? " " : "  ·  ") + load;
}
